import React, { useEffect, useMemo, useRef, useState } from 'react';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { useSearchParams } from 'react-router-dom';

import LoadingSpinner from '../../../components/LoadingSpinner';
import Notification from '../../../components/Notification';
import MultiSelect from '../../../portal/components/MultiSelect';
import logError from '../../../utils/errorUtil';
import TableSortButton from '../../components/guidedInterface/components/TableSortButton';
import { useAuth } from '../../context/wms-context';
import {
    defaultArrowDirections,
    historyTableHeaderValues,
    rmaHistoryFilterTypes,
    rmaHistoryRmaStatusFilterTypes,
    tableHeaderValues,
} from './data';
import { doesRmaMatchTableFilters, getHistoryRmas, getPendingRmas } from './functions';
import WMS_RMAReview from './WMS_RMAReview';

export default function WMS_RMA() {
    const { currentUser } = useAuth();
    const [searchParams, setSearchParams] = useSearchParams();
    const deepLinkHandledRef = useRef(false);
    const [pendingRmas, setPendingRmas] = useState([]);
    const [pendingRmasCopy, setPendingRmasCopy] = useState([]);
    const [historyRmas, setHistoryRmas] = useState([]);
    const [historyLoaded, setHistoryLoaded] = useState(false);
    const [viewMode, setViewMode] = useState('active');
    const [loading, setLoading] = useState(true);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [searchValue, setSearchValue] = useState('');
    const [arrowDirections, setArrowDirections] = useState(defaultArrowDirections);
    const [selectedRmaId, setSelectedRmaId] = useState(null);
    const [selectedReadOnly, setSelectedReadOnly] = useState(false);
    const [toast, setToast] = useState(null);

    const [historyFilters, setHistoryFilters] = useState([]);
    const [historyRmaStatusFilters, setHistoryRmaStatusFilters] = useState([]);
    const historyStatusFilterOptions = Object.values(rmaHistoryFilterTypes).map((status) => ({
        label: status,
        value: status,
    }));
    const historyRmaStatusFilterOptions = Object.values(rmaHistoryRmaStatusFilterTypes).map((status) => ({
        label: status,
        value: status,
    }));

    const isHistory = viewMode === 'history';
    const activeHeaders = isHistory ? historyTableHeaderValues : tableHeaderValues;
    const activeRows = isHistory ? historyRmas : pendingRmas;

    const hostname = window.location.hostname;
    const isPacAirHost = hostname === 'pac-air.com' || hostname.endsWith('.pac-air.com');
    const rmaEntryUrl = isPacAirHost ? 'https://rma.pac-air.com/rma?internal=true' : '/rma?internal=true';

    const hasSortApplied = useMemo(() => {
        return Object.values(arrowDirections).some((directionData) => directionData.direction !== null);
    }, [arrowDirections]);

    useEffect(() => {
        loadPendingRmas();
    }, []);

    useEffect(() => {
        if (!toast) return;
        const timeoutId = setTimeout(() => setToast(null), 5000);
        return () => clearTimeout(timeoutId);
    }, [toast]);

    useEffect(() => {
        const rmaRequestId = searchParams.get('rma-request');
        if (!rmaRequestId || deepLinkHandledRef.current || loading) return;

        const openFromDeepLink = async () => {
            deepLinkHandledRef.current = true;
            const pendingMatch = pendingRmas.find((rmaRow) => String(rmaRow.id) === String(rmaRequestId));
            if (pendingMatch) {
                setSelectedReadOnly(false);
                setSelectedRmaId(String(rmaRequestId));
                return;
            }

            try {
                let historyData = historyRmas;
                if (!historyLoaded) {
                    setHistoryLoading(true);
                    historyData = await getHistoryRmas(currentUser);
                    setHistoryRmas(historyData);
                    setHistoryLoaded(true);
                }
                setViewMode('history');
                setSelectedReadOnly(true);
                setSelectedRmaId(String(rmaRequestId));
            } catch (error) {
                logError(
                    error,
                    `openFromDeepLink: Failed to open RMA ${rmaRequestId} | userId: ${
                        currentUser?.authentication?.userId || 'N/A'
                    }`,
                    false
                );
                deepLinkHandledRef.current = false;
            } finally {
                setHistoryLoading(false);
            }
        };

        openFromDeepLink();
    }, [loading, pendingRmas, searchParams, historyLoaded, historyRmas, currentUser]);

    const loadPendingRmas = async () => {
        try {
            setLoading(true);
            const pendingRmaData = await getPendingRmas(currentUser);
            setPendingRmas(pendingRmaData);
            setPendingRmasCopy(pendingRmaData);
        } catch (error) {
            logError(
                error,
                `loadPendingRmas: Failed to load pending RMAs | userId: ${
                    currentUser?.authentication?.userId || 'N/A'
                } | userEmail: ${currentUser?.authentication?.userEmail || 'N/A'}`,
                false
            );
            setPendingRmas([]);
            setPendingRmasCopy([]);
        } finally {
            setLoading(false);
        }
    };

    const loadHistoryRmas = async () => {
        if (historyLoaded) return;
        try {
            setHistoryLoading(true);
            const historyRmaData = await getHistoryRmas(currentUser);
            setHistoryRmas(historyRmaData);
            setHistoryLoaded(true);
        } catch (error) {
            logError(
                error,
                `loadHistoryRmas: Failed to load RMA history | userId: ${
                    currentUser?.authentication?.userId || 'N/A'
                } | userEmail: ${currentUser?.authentication?.userEmail || 'N/A'}`,
                false
            );
            setHistoryRmas([]);
        } finally {
            setHistoryLoading(false);
        }
    };

    const handleToggleView = () => {
        const next = isHistory ? 'active' : 'history';
        setViewMode(next);
        setSearchValue('');
        setArrowDirections(defaultArrowDirections);
        if (next === 'history') {
            loadHistoryRmas();
            setHistoryFilters([]);
            setHistoryRmaStatusFilters([]);
        }
    };

    const renderSortButton = (columnName) => {
        return (
            <TableSortButton arrowDirections={arrowDirections} setArrowDirections={setArrowDirections} name={columnName} />
        );
    };

    const resetSort = () => {
        if (isHistory) {
            setHistoryRmas([...historyRmas]);
        } else {
            setPendingRmas([...pendingRmasCopy]);
        }
        setArrowDirections(defaultArrowDirections);
    };

    const sortedAndFilteredRmas = useMemo(() => {
        return [...activeRows]
            .sort((a, b) => {
                for (const [key, value] of Object.entries(arrowDirections)) {
                    if (value.direction === null) continue;

                    const direction = value.direction === 'asc' ? 1 : -1;
                    const aValue = a[key];
                    const bValue = b[key];

                    if (aValue === null || aValue === undefined) return 1 * direction;
                    if (bValue === null || bValue === undefined) return -1 * direction;

                    if (aValue > bValue) return direction;
                    if (aValue < bValue) return -direction;
                }
                return 0;
            })
            .filter((rmaRow) =>
                doesRmaMatchTableFilters(rmaRow, searchValue, isHistory, historyFilters, historyRmaStatusFilters)
            );
    }, [activeRows, arrowDirections, searchValue, isHistory, historyFilters, historyRmaStatusFilters]);

    const renderLinkedCell = (name, link) => {
        if (!name) return 'N/A';
        if (!link) return name;

        return (
            <a
                onClick={(e) => e.stopPropagation()}
                className='text-blue-800 underline hover:text-blue'
                target='_blank'
                rel='noopener noreferrer'
                href={link}
            >
                {name}
            </a>
        );
    };

    if (selectedRmaId) {
        return (
            <WMS_RMAReview
                rmaId={selectedRmaId}
                readOnly={selectedReadOnly}
                onBack={async (toastInfo) => {
                    if (toastInfo) setToast(toastInfo);
                    setSelectedRmaId(null);
                    setSelectedReadOnly(false);
                    if (searchParams.has('rma-request')) {
                        searchParams.delete('rma-request');
                        setSearchParams(searchParams, { replace: true });
                    }
                    deepLinkHandledRef.current = false;
                    if (!selectedReadOnly) {
                        try {
                            await loadPendingRmas();
                        } catch {}
                    }
                }}
            />
        );
    }

    const isLoadingCurrent = isHistory ? historyLoading : loading;

    return (
        <section className='h-[calc(100vh-64px)] pb-10'>
            <Notification
                showSavedNotification={Boolean(toast)}
                setShowSavedNotification={(isVisible) => {
                    if (!isVisible) setToast(null);
                }}
                title={toast?.title || ''}
                desc={toast?.desc || ''}
                status={toast?.status || 'success'}
            />
            <header className='flex justify-center items-center pt-4 pb-4 w-full'>
                <section className='grid grid-cols-[1fr_auto_1fr] items-center px-4 w-full'>
                    <form onSubmit={(e) => e.preventDefault()} className='flex flex-row gap-2 items-center'>
                        <h1>Search:</h1>
                        <input
                            className='px-2 py-1 rounded-md border border-gray-700 outline-none focus:ring focus:ring-blue-200 focus:border-black hover:bg-gray-100'
                            onChange={(e) => setSearchValue(e.target.value)}
                            value={searchValue}
                            disabled={isLoadingCurrent}
                        />
                        <button className='p-1 w-8 rounded-md bg-blue-primary hover:bg-blue-900' type='button'>
                            <MagnifyingGlassIcon className='text-white' />
                        </button>
                    </form>
                    <div className='flex justify-center items-center px-4'>
                        <h1 className='text-3xl whitespace-nowrap'>{isHistory ? 'RMA History' : 'Pending RMA Review'}</h1>
                        {hasSortApplied && (
                            <button
                                className='p-1 ml-4 w-24 text-white rounded-md bg-blue-primary hover:bg-blue-900'
                                onClick={resetSort}
                            >
                                Reset Sort
                            </button>
                        )}
                    </div>
                    <div className='flex flex-row flex-nowrap gap-4 justify-end items-center'>
                        {isHistory && (
                            <>
                                <div className='flex flex-col w-[180px]'>
                                    <label className='mb-1 text-sm font-medium text-gray-700'>Filter by Status</label>
                                    <MultiSelect
                                        value={historyFilters}
                                        onChange={setHistoryFilters}
                                        options={historyStatusFilterOptions}
                                        placeholder='ALL'
                                    />
                                </div>
                                <div className='flex flex-col w-[180px]'>
                                    <label className='mb-1 text-sm font-medium text-gray-700'>Filter by RMA Status</label>
                                    <MultiSelect
                                        value={historyRmaStatusFilters}
                                        onChange={setHistoryRmaStatusFilters}
                                        options={historyRmaStatusFilterOptions}
                                        placeholder='ALL'
                                    />
                                </div>
                            </>
                        )}
                        <a
                            href={rmaEntryUrl}
                            target='_blank'
                            rel='noopener noreferrer'
                            className='inline-flex items-center justify-center px-4 py-1 whitespace-nowrap text-white rounded-md bg-blue-primary hover:bg-blue-900'
                        >
                            RMA Entry
                        </a>
                        <button
                            className='inline-flex items-center justify-center px-4 py-1 whitespace-nowrap text-white rounded-md bg-blue-primary hover:bg-blue-900'
                            onClick={handleToggleView}
                        >
                            {isHistory ? 'Active Orders' : 'History'}
                        </button>
                    </div>
                </section>
            </header>

            <section>
                <table className='w-full table-auto'>
                    <thead className='sticky top-0 z-10'>
                        <tr className='bg-blue-primary'>
                            {activeHeaders.map((headerValue, index) => {
                                const { text, key, allowSort } = headerValue;
                                return (
                                    <th
                                        key={index}
                                        className='p-2 font-medium text-left text-white border-b dark:border-slate-600'
                                    >
                                        <div className='flex justify-between items-center'>
                                            <p>{text}</p>
                                            {allowSort && renderSortButton(key)}
                                        </div>
                                    </th>
                                );
                            })}
                        </tr>
                    </thead>
                    <tbody>
                        {isLoadingCurrent ? (
                            <tr>
                                <td className='py-8 text-center' colSpan={activeHeaders.length}>
                                    <div className='flex justify-center items-center'>
                                        <LoadingSpinner className={'h-fit'} />
                                    </div>
                                </td>
                            </tr>
                        ) : (
                            sortedAndFilteredRmas.map((rmaRow) => {
                                const isProcessingRow =
                                    !isHistory && (rmaRow.statusLabel === 'Processing' || !rmaRow.canReview);
                                return (
                                    <tr
                                        key={rmaRow.id}
                                        className={`border-b border-slate-200 even:bg-gray-100 ${
                                            isProcessingRow ? 'opacity-60 bg-gray-200' : 'hover:bg-gray-200'
                                        }`}
                                    >
                                        <td className='p-2 text-left'>
                                            {renderLinkedCell(rmaRow.invoiceName, rmaRow.invoiceLink)}
                                        </td>
                                        <td className='p-2 text-left'>
                                            {renderLinkedCell(rmaRow.releaseName, rmaRow.releaseLink)}
                                        </td>
                                        <td className='p-2 text-left'>
                                            {renderLinkedCell(rmaRow.salesOrderName, rmaRow.salesOrderLink)}
                                        </td>
                                        <td className='p-2 text-left'>{rmaRow.customerPoNumber || 'N/A'}</td>
                                        <td className='p-2 text-left'>
                                            {renderLinkedCell(rmaRow.assignedToName, rmaRow.assignedToLink)}
                                        </td>
                                        <td className='p-2 text-left'>{rmaRow.createdDateDisplay || 'N/A'}</td>
                                        <td
                                            className={`p-2 text-left ${
                                                rmaRow.submissionStatus === 'Approved'
                                                    ? 'text-green-500'
                                                    : rmaRow.submissionStatus === 'Denied'
                                                    ? 'text-red-500'
                                                    : ''
                                            }`}
                                        >
                                            {rmaRow.statusLabel || 'Operations'}
                                        </td>
                                        {isHistory && (
                                            <td className='p-2 text-left'>
                                                {renderLinkedCell(rmaRow.rmaNumber, rmaRow.rmaLink)}
                                            </td>
                                        )}
                                        {isHistory && <td className='p-2 text-left'>{rmaRow.rmaStatus || 'N/A'}</td>}
                                        {isHistory && (
                                            <td className='p-2 text-left'>{rmaRow.rmaCreatedDateDisplay || 'N/A'}</td>
                                        )}
                                        {isHistory && (
                                            <td className='p-2 text-left'>{rmaRow.rmaExpirationDateDisplay || 'N/A'}</td>
                                        )}
                                        <td className='px-1 py-2 text-left'>
                                            {renderLinkedCell(rmaRow.customerName, rmaRow.customerLink)}
                                        </td>
                                        <td className='p-2 text-left'>
                                            {renderLinkedCell(rmaRow.contactName, rmaRow.contactLink)}
                                        </td>
                                        <td className='p-2 text-left'>{rmaRow.rmaTotalDisplay}</td>
                                        <td className='p-2 text-left'>
                                            <button
                                                className={`px-3 py-1 text-white rounded-md ${
                                                    isProcessingRow
                                                        ? 'bg-gray-500 cursor-not-allowed'
                                                        : 'bg-blue-primary hover:bg-blue-900'
                                                }`}
                                                onClick={() => {
                                                    if (isProcessingRow) return;
                                                    setSelectedReadOnly(isHistory);
                                                    setSelectedRmaId(rmaRow.id);
                                                }}
                                                disabled={isProcessingRow}
                                            >
                                                {isHistory ? 'View' : 'Review'}
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </section>
        </section>
    );
}
